import {Link} from '@primer/react'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {lazy, memo, Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import {ProgressivePullRequestDiffEntry} from './progressive-diff/ProgressivePullRequestDiffEntry'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useCollapsedDiffStatus,
  useCommentsPreference,
  useDiffEntriesDigestsWithFilter,
  useDiffSummary,
  useDiffSummaryCount,
  useIsSelectedFile,
  useLineSpacingPreference,
  useProgressiveDiffActions,
  useProgressiveDiffEntryByPathDigest,
} from '../stores/selectors'
import {estimateDiffSize} from '../helpers/estimate-diff-size'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import styles from './PullRequestDiffsList.module.css'
import {clsx} from 'clsx'
import {hpcAttrs} from '@github-ui/hpc'

const PullRequestDiffsAnimation = lazy(async () => {
  const module = await import('./PullRequestDiffsAnimation')
  return {default: module.PullRequestDiffsAnimation}
})

export interface PullRequestDiffsListProps {
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const diffsParentRef = useRef<HTMLDivElement>(null)
  const diffEntryDigests = useDiffEntriesDigestsWithFilter()
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue} = useProgressiveDiffActions()
  const showUpdatedHeader = useIsOverviewEnabled()

  useEffect(() => {
    startQueue({autoLoadAllDiffs: true})
  }, [startQueue])

  const {onScrollToAndFocusEntry, getTargetableEntriesMap} = useScrollToAndFocus()

  const targetableEntriesMap = getTargetableEntriesMap()

  const callbackRef = useCallback(
    (pathDigest: string, node: HTMLDivElement | null) => {
      if (node) {
        targetableEntriesMap.set(pathDigest, node)
      } else {
        targetableEntriesMap.delete(pathDigest)
      }
      return () => {
        targetableEntriesMap.delete(pathDigest)
      }
    },
    [targetableEntriesMap],
  )

  const diffSummariesCount = useDiffSummaryCount()

  const totalVisibleEntries = diffEntryDigests.length
  const shouldShowWhimsy = totalVisibleEntries >= 15

  const stickyObserver = useStickyObserver()

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (diffEntryDigests.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div
      ref={diffsParentRef}
      {...hpcAttrs()}
      data-testid="progressive-diffs-list"
      className={clsx('d-flex flex-column gap-3', showUpdatedHeader && 'pt-2')}
    >
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      {diffEntryDigests.map(pathDigest => {
        return (
          <DiffEntryWrapper key={pathDigest} pathDigest={pathDigest} ref={callbackRef} stickyObserver={stickyObserver}>
            <ProgressivePullRequestDiffEntry
              diffManuallyExpanded={diffManuallyExpanded}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              onScrollToAndFocusEntry={onScrollToAndFocusEntry}
              pathDigest={pathDigest}
              repository={repository}
            />
          </DiffEntryWrapper>
        )
      })}
      {shouldShowWhimsy && (
        <ErrorBoundary boundaryName="PullRequestDiffsListAnimation" fallback={null}>
          <Suspense fallback={null}>
            <PullRequestDiffsAnimation />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

/**
 * Single IntersectionObserver that toggles data-sticky-enabled on diff entries
 * near the viewport. Only ~5-8 entries have sticky headers at any time,
 * instead of all 300+.
 */
function useStickyObserver() {
  const [observer] = useState(() => {
    if (typeof IntersectionObserver === 'undefined') return null
    return new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            ;(entry.target as HTMLElement).setAttribute('data-sticky-enabled', '')
          } else {
            ;(entry.target as HTMLElement).removeAttribute('data-sticky-enabled')
          }
        }
      },
      {rootMargin: '300% 0px'},
    )
  })

  useEffect(() => {
    return () => observer?.disconnect()
  }, [observer])

  return observer
}

function DiffEntryWrapper({
  pathDigest,
  ref,
  children,
  stickyObserver,
}: {
  pathDigest: string
  ref: (pathDigest: string, node: HTMLDivElement | null) => (() => void) | void
  children: React.ReactNode
  stickyObserver: IntersectionObserver | null
}) {
  const progressiveDiffEntry = useProgressiveDiffEntryByPathDigest(pathDigest)
  const filePath = progressiveDiffEntry?.path ?? ''
  const diffSummary = useDiffSummary(filePath)
  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()
  const isCollapsed = useCollapsedDiffStatus(filePath)
  const isSelected = useIsSelectedFile(pathDigest)
  const estimatedHeight = estimateDiffSize({diffSummary, commentPreference, lineSpacingPreference, isCollapsed})

  return (
    <div
      className={styles.diffEntry}
      style={{contentVisibility: isSelected ? 'visible' : 'auto', containIntrinsicSize: `auto ${estimatedHeight}px`}}
      ref={useCallback(
        (node: HTMLDivElement | null) => {
          if (node && stickyObserver) stickyObserver.observe(node)
          const refCleanup = ref(pathDigest, node)
          return () => {
            if (node && stickyObserver) stickyObserver.unobserve(node)
            refCleanup?.()
          }
        },
        [ref, pathDigest, stickyObserver],
      )}
    >
      {children}
    </div>
  )
}
